import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { displayDeliveryPickup, displayDeliveryShipping } from '../../../util/configHelpers';

import { Heading } from '../../../components';
import SectionMapMaybe from '../SectionMapMaybe';
import { getHowToGetItOptionBoxes } from '../listingFieldHelpers';

import css from './SectionHowToGetIt.module.css';

const OptionBox = ({ label, value, className }) => (
  <div className={classNames(css.optionBox, className)}>
    <span className={css.optionLabel}>{label}</span>
    <span className={css.optionValue}>{value}</span>
  </div>
);

/**
 * Pickup and delivery option boxes, location address, and map for the listing page.
 *
 * @component
 */
const SectionHowToGetIt = props => {
  const {
    className,
    rootClassName,
    publicData,
    metadata,
    geolocation,
    listingId,
    mapsConfig,
    listingTypeConfig,
    listingFieldConfigs,
    categoryConfiguration,
  } = props;

  const intl = useIntl();
  const { pickupEnabled, shippingEnabled, location } = publicData || {};
  const displayPickup = listingTypeConfig && displayDeliveryPickup(listingTypeConfig);
  const displayShipping = listingTypeConfig && displayDeliveryShipping(listingTypeConfig);

  const customOptionBoxes = getHowToGetItOptionBoxes({
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    intl,
  });

  const pickupLabel = intl.formatMessage({ id: 'ListingPage.pickupOption' });
  const deliveryLabel = intl.formatMessage({ id: 'ListingPage.deliveryOption' });
  const availableLabel = intl.formatMessage({ id: 'ListingPage.pickupAvailable' });
  const deliveryAvailableLabel = intl.formatMessage({ id: 'ListingPage.deliveryAvailable' });

  const hasCustomPickup = customOptionBoxes.some(
    box =>
      box.label?.toLowerCase().includes('pickup') || box.key?.toLowerCase().includes('pickup')
  );
  const hasCustomDelivery = customOptionBoxes.some(
    box =>
      box.label?.toLowerCase().includes('delivery') || box.key?.toLowerCase().includes('delivery')
  );

  const optionBoxes = [...customOptionBoxes];

  if (displayPickup && pickupEnabled && !hasCustomPickup) {
    optionBoxes.unshift({
      key: 'pickup-enabled',
      label: pickupLabel,
      value: availableLabel,
    });
  }

  if (displayShipping && shippingEnabled && !hasCustomDelivery) {
    optionBoxes.push({
      key: 'delivery-enabled',
      label: deliveryLabel,
      value: deliveryAvailableLabel,
    });
  }

  const pickupFromFlags = displayPickup && pickupEnabled;
  const address = location?.address;
  const hasMap = !!geolocation;
  const hasOptionBoxes = optionBoxes.length > 0;
  const hasContent = hasOptionBoxes || address || hasMap;

  if (!hasContent) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes} id="listing-location">
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.howToGetItTitle" />
      </Heading>

      {hasOptionBoxes ? (
        <div className={css.optionBoxes}>
          {optionBoxes.map(({ key, label, value }) => (
            <OptionBox key={key} label={label} value={value} />
          ))}
        </div>
      ) : null}

      {pickupFromFlags && address ? <p className={css.pickupAddress}>{address}</p> : null}

      {address && !pickupFromFlags ? (
        <div className={css.locationBlock}>
          <Heading as="h3" rootClassName={css.locationHeading}>
            <FormattedMessage id="ListingPage.locationTitle" />
          </Heading>
          <p className={css.address}>{address}</p>
        </div>
      ) : null}

      {hasMap ? (
        <SectionMapMaybe
          className={css.mapSection}
          geolocation={geolocation}
          publicData={publicData}
          listingId={listingId}
          mapsConfig={mapsConfig}
          showHeading={pickupFromFlags && !!address}
        />
      ) : null}
    </section>
  );
};

export default SectionHowToGetIt;
